import { useEffect, useState, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { pget, ppost, fmtStamp, STATUS, JOB_STATUS } from "./portalApi";
import { TextAssist } from "@/components/TextAssist";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Wrench, Plus, ChevronRight, Clock, CircleDot, MessageSquare, Send, Camera, Check, MapPin, CheckCircle2, Archive } from "lucide-react";

const EVENT_LABEL = { created: "Request opened", status: "Status changed", priority: "Priority updated", due: "Due date updated", attachment: "Attachment added" };
const JOURNEY_ICON = { reported: Camera, dispatched: Send, accepted: Check, enroute: MapPin, completed: CheckCircle2, closed: Archive };

// Vertical progress rail for a dispatched field-service job — resident-friendly, real timestamps.
function JobJourney({ job }) {
  return (
    <div data-testid="portal-job-journey">
      <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" /> Progress</div>
      {job.cancelled && (
        <p data-testid="portal-job-cancelled" className="mb-3 rounded-lg bg-sand-100 px-3 py-2 text-xs font-semibold text-ink/60">This job was cancelled. Text or call the concierge if you still need help.</p>
      )}
      <div>
        {job.steps.map((s, i) => {
          const Icon = JOURNEY_ICON[s.key] || CircleDot;
          const last = i === job.steps.length - 1;
          return (
            <div key={s.key} data-testid={`portal-job-step-${s.key}`} data-state={s.state} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border-2 transition-colors ${
                  s.state === "done" ? "border-emerald-500 bg-emerald-500 text-white"
                    : s.state === "current" ? "border-redline-500 bg-white text-redline-600 shadow-[0_0_0_4px_rgba(229,52,46,0.12)]"
                      : "border-sand-200 bg-sand-50 text-ink/25"}`}>
                  <Icon className="h-4 w-4" strokeWidth={2.25} />
                </span>
                {!last && <span className={`w-0.5 flex-1 my-1 rounded-full ${s.state === "done" ? "bg-emerald-500" : "bg-sand-200"}`} />}
              </div>
              <div className={`pb-5 ${last ? "" : "min-h-[2.5rem]"}`}>
                <div className={`text-sm font-semibold leading-tight ${s.state === "upcoming" ? "text-ink/35" : "text-ink"}`}>{s.label}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {s.at ? fmtStamp(s.at) : s.state === "current" ? "In progress" : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PortalRequests() {
  const { me } = useOutletContext() || {};
  const moduleLabel = (me?.community?.requests_label || "").trim() || "My Requests";
  // Tracking-only communities (answers_only) take requests by text/call — residents can
  // watch a dispatched job here but the portal doesn't let them file a new ticket.
  const canFile = (me?.community?.mode || "answers_requests") !== "answers_only";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [topics, setTopics] = useState([]);
  const [topicDisplay, setTopicDisplay] = useState("auto");
  const [form, setForm] = useState({ category: "", summary: "", details: "" });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(() => {
    pget("/resident/requests").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Topics the community configured for the New Request screen (managers control these).
  useEffect(() => {
    pget("/resident/request-categories").then((r) => {
      const t = r.data.topics || [];
      setTopics(t);
      setTopicDisplay(r.data.display || "auto");
      setForm((f) => ({ ...f, category: f.category || (t[0]?.code || "other") }));
    }).catch(() => {
      setTopics([{ code: "other", label: "Something else" }]);
      setForm((f) => ({ ...f, category: f.category || "other" }));
    });
  }, []);

  // Adaptive: honor an explicit choice, else pills for a short list and a dropdown once it grows.
  const useDropdown = topicDisplay === "dropdown" || (topicDisplay === "auto" && topics.length > 6);

  const submit = async () => {
    if (!form.summary.trim()) { toast.error("Tell us what's going on first"); return; }
    setSaving(true);
    try {
      const r = await ppost("/resident/requests", form);
      toast.success(`Request ${r.data.ref || ""} submitted — the team is on it 🎉`);
      setOpen(false); setForm({ category: topics[0]?.code || "other", summary: "", details: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't submit that"); }
    finally { setSaving(false); }
  };

  const openDetail = async (r) => {
    try { const d = await pget(`/resident/requests/${r.id}`); setDetail(d.data); setCommentText(""); }
    catch { toast.error("Couldn't load that request"); }
  };

  const postComment = async () => {
    if (!detail || !commentText.trim()) return;
    setPosting(true);
    try {
      await ppost(`/resident/requests/${detail.id}/comments`, { text: commentText.trim() });
      const d = await pget(`/resident/requests/${detail.id}`);
      setDetail(d.data); setCommentText("");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send that"); }
    finally { setPosting(false); }
  };

  // Map a request's category code to a human label (from the community's configured topics),
  // falling back to a tidied version of the code so old/hidden categories still read cleanly.
  const topicLabel = useMemo(() => Object.fromEntries(topics.map((t) => [t.code, t.label])), [topics]);
  const catLabel = (code) => topicLabel[code] || (code ? code.replace(/_i\d+$/, "").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

  return (
    <div className="space-y-6" data-testid="portal-requests-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2"><Wrench className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> {moduleLabel}</h1>
          <p className="text-muted-foreground mt-1">{canFile
            ? "Everything your household has reported — however it came in: text, call, or right here."
            : "Track everything your household has reported. To start something new, text or call your concierge."}</p>
        </div>
        {canFile && (
          <button data-testid="portal-new-request-btn" onClick={() => setOpen(true)} className="rounded-full bg-redline-500 text-white font-semibold px-4 py-2.5 flex items-center gap-2 hover:bg-redline-600 transition-colors"><Plus className="h-4 w-4" strokeWidth={2.5} /> New request</button>
        )}
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
        : items.length === 0 ? <div className="bg-white rounded-2xl border border-sand-200 p-12 text-center text-muted-foreground">{canFile ? <>Nothing yet. Tap &ldquo;New request&rdquo; or just text the concierge.</> : <>Nothing yet. Text or call your concierge to report something — you can track it here.</>}</div>
        : <div className="space-y-2.5" data-testid="portal-requests-list">
          {items.map((r) => {
            const pill = r.job_status ? (JOB_STATUS[r.job_status] || JOB_STATUS.reported) : (STATUS[r.status] || STATUS.open);
            return (
            <button key={r.id} data-testid={`portal-request-${r.id}`} onClick={() => openDetail(r)}
              className="w-full bg-white rounded-2xl border border-sand-200 p-4 flex items-center gap-4 text-left hover:border-ink transition-colors">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-ink truncate">{r.title || r.summary}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{r.ref} · {fmtStamp(r.created_at)}{r.category ? ` · ${catLabel(r.category)}` : ""}</div>
              </div>
              <span data-testid={`portal-request-status-${r.id}`} className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${pill.cls}`}>{pill.label}</span>
              <ChevronRight className="h-4 w-4 text-sand-400 shrink-0" />
            </button>
          );})}
        </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="portal-request-dialog">
          <DialogHeader><DialogTitle className="font-heading">New request</DialogTitle><DialogDescription>Goes straight to your community team — you can track it here.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Topic</label>
              {useDropdown ? (
                <select data-testid="portal-req-cat-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1.5 w-full rounded-xl border border-sand-200 bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30">
                  {topics.map((c) => (
                    <option key={c.code} value={c.code} data-testid={`portal-req-cat-opt-${c.code}`}>{c.label}</option>
                  ))}
                </select>
              ) : (
                <div className="flex gap-2 flex-wrap mt-1.5">
                  {topics.map((c) => (
                    <button key={c.code} data-testid={`portal-req-cat-${c.code}`} onClick={() => setForm({ ...form, category: c.code })}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${form.category === c.code ? "bg-redline-500 text-white border-redline-500" : "bg-white text-ink-700 border-sand-200 hover:border-ink"}`}>{c.label}</button>
                  ))}
                </div>
              )}
            </div>
            <input data-testid="portal-req-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="What's going on? (short summary)"
              className="w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <div className="flex items-center justify-end">
              <TextAssist resident value={form.details} onChange={(next) => setForm({ ...form, details: next })}
                testId="portal-req-assist" micTitle="Say what's going on" />
            </div>
            <textarea data-testid="portal-req-details" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={3} placeholder="Any helpful details — where, since when, photos coming, etc."
              className="w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 resize-y" />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Cancel</button>
            <button data-testid="portal-req-submit" disabled={saving} onClick={submit} className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-60">{saving ? "Sending…" : "Submit request"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-md" data-testid="portal-request-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading pr-6">{detail.title || detail.summary}</DialogTitle>
                <DialogDescription>{detail.ref} · opened {fmtStamp(detail.created_at)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {(() => {
                  const pill = detail.job ? (JOB_STATUS[detail.job.status] || JOB_STATUS.reported) : (STATUS[detail.status] || STATUS.open);
                  return <span data-testid="portal-request-detail-status" className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${pill.cls}`}>{detail.job ? detail.job.status_label : pill.label}</span>;
                })()}
                {detail.details && <p className="text-sm text-ink-700 whitespace-pre-wrap">{detail.details}</p>}
                {detail.job ? (
                  <JobJourney job={detail.job} />
                ) : (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Timeline</div>
                  {(detail.events || []).length === 0 ? <p className="text-sm text-muted-foreground">We&apos;ll show updates here as the team works on it.</p>
                    : <div className="space-y-2.5">
                      {[...detail.events].reverse().map((e, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <CircleDot className="h-3.5 w-3.5 text-redline-500 mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm text-ink font-medium">{EVENT_LABEL[e.kind] || e.kind}{e.detail ? ` — ${e.detail}` : ""}</div>
                            <div className="text-[11px] text-muted-foreground">{fmtStamp(e.created_at)}</div>
                          </div>
                        </div>
                      ))}
                    </div>}
                </div>
                )}

                {/* Messages — the resident ⇄ office conversation on this request */}
                <div data-testid="portal-request-comments">
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Messages</div>
                  {(detail.comments || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No messages yet. Add a comment below and the team will see it.</p>
                  ) : (
                    <div className="space-y-2.5 mb-3">
                      {detail.comments.map((c) => {
                        const mine = c.author_type === "resident";
                        return (
                          <div key={c.id} data-testid={`portal-comment-${c.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 ${mine ? "bg-redline-500 text-white" : "bg-sand-100 text-ink"}`}>
                              <div className={`text-[11px] font-semibold mb-0.5 ${mine ? "text-white/80" : "text-ink/50"}`}>{mine ? "You" : (c.author_name || "The office")}</div>
                              <div className="text-sm whitespace-pre-wrap break-words">{c.text}</div>
                              <div className={`text-[10px] mt-0.5 ${mine ? "text-white/60" : "text-ink/40"}`}>{fmtStamp(c.created_at)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {detail.can_comment ? (
                    <div className="mt-2">
                      <div className="flex items-center justify-end">
                        <TextAssist resident value={commentText} onChange={setCommentText}
                          testId="portal-comment-assist" micTitle="Dictate your message" />
                      </div>
                      <div className="flex items-end gap-2">
                        <textarea data-testid="portal-comment-input" value={commentText} onChange={(e) => setCommentText(e.target.value)} rows={2}
                          placeholder="Write a message to the team…"
                          className="flex-1 rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 resize-y" />
                        <button data-testid="portal-comment-send" disabled={posting || !commentText.trim()} onClick={postComment}
                          className="rounded-full bg-redline-500 text-white p-2.5 hover:bg-redline-600 transition-colors disabled:opacity-50 shrink-0"><Send className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">This request is closed. Open a new one to reach the team.</p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
